import dayjs from 'src/utils/dayjs';
import { toMexicoCityInstant } from 'src/utils/momentLocalTimezone';
import {
  createTransaction,
  getTransactionById,
  getAllTransactions,
  downloadInvoiceById,
  getMovements,
  downloadExportedDistributors,
  assignTransctionToEquipment,
  getTransactionByContractId,
  downloadStatmentResume,
  downloadClientTransactions,
  getTransactionDetailById
} from 'src/services/transactionsService';
import { asyncActionCreator } from 'src/utils/loadingUtils';
import {
  TransactionsCategory,
  TransactionsStatus,
  TransactionsType
} from 'src/utils/constants/transactions';
import { FirstDayOfMonth, SnackBarVariants } from 'src/utils/constants/common';
import { createUpfrontRent } from 'src/services/contractService';
import { selectTransactionsQueryOptions } from '../selectors/transactionSelectors';
import { showMessage } from './snackBarActions';

export const LOAD_TRANSACTIONS = asyncActionCreator('LOAD_TRANSACTIONS');
export const DOWNLOAD_TRANSACTIONS_LIST = asyncActionCreator(
  'DOWNLOAD_TRANSACTIONS_LIST'
);
export const CREATE_MANUAL_TRANSACTION = asyncActionCreator(
  'CREATE_MANUAL_TRANSACTION'
);
export const LOAD_MOVEMENTS_BY_CONTACT = asyncActionCreator(
  'LOAD_MOVEMENTS_BY_CONTACT'
);
export const CLEAR_TRANSACTIONS = 'CLEAR_TRANSACTIONS';
export const SAVE_TRANSACTION_OPTIONS = 'SAVE_TRANSACTION_OPTIONS';
export const SAVE_ACCOUNT_STATEMENT_OPTIONS = 'SAVE_ACCOUNT_STATEMENT_OPTIONS';
export const CLEAR_TRANSACTION_OPTIONS = 'CLEAR_TRANSACTION_OPTIONS';
export const DOWNLOAD_INVOICE = asyncActionCreator('DOWNLOAD_INVOICE');
export const DOWNLOAD_ACCOUNT_STATEMENT_RESUME = asyncActionCreator('DOWNLOAD_ACCOUNT_STATEMENT_RESUME');
export const DOWNLOAD_CLIENT_TRANSACTIONS = asyncActionCreator('DOWNLOAD_CLIENT_TRANSACTIONS');
export const ASSIGN_TRANSACTION_TO_CONTRACT = asyncActionCreator(
  'ASSIGN_TRANSACTION_TO_CONTRACT'
);
export const LOAD_TRANSACTION_DETAIL = asyncActionCreator(
  'LOAD_TRANSACTION_DETAIL'
);
export const CLEAR_TRANSACTION_DETAIL = 'CLEAR_TRANSACTION_DETAIL';

// Transaction by contract 
export const LOAD_CONTRACT_TRANSACTIONS = asyncActionCreator('LOAD_CONTRACT_TRANSACTIONS');
export const CLEAR_CONTRACT_TRANSACTIONS = 'CLEAR_CONTRACT_TRANSACTIONS';
export const CLEAR_CONTRACT_TRANSACTION_OPTIONS = 'CLEAR_CONTRACT_TRANSACTION_OPTIONS';
export const SAVE_CONTRACT_TRANSACTION_OPTIONS = 'SAVE_CONTRACT_TRANSACTION_OPTIONS';

function getOptions(getState, sendLimit = true) {
  const queryOpts = selectTransactionsQueryOptions(getState());
  const reqOptions = {};

  if (queryOpts.page > 0) {
    reqOptions.offset =
      queryOpts.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }
  if (queryOpts.id) {
    reqOptions.id = queryOpts.id;
  }
  if (queryOpts.clientName) {
    reqOptions.clientName = queryOpts.clientName;
  }
  if (queryOpts.month) {
    reqOptions.afterDate = dayjs(queryOpts.month)
      .date(FirstDayOfMonth)
      .format('YYYY-MM-DD');
    reqOptions.beforeDate = dayjs(queryOpts.month)
      .endOf('month')
      .format('YYYY-MM-DD');
  }
  if (queryOpts.beforeDate) {
    reqOptions.beforeDate = toMexicoCityInstant(queryOpts.beforeDate)
      .endOf('day')
      .toISOString();
  }
  if (queryOpts.contractId) {
    reqOptions.contractId = queryOpts.contractId;
  }
  if (queryOpts.folio) {
    reqOptions.folio = queryOpts.folio;
  }
  if (queryOpts.paymentStatus) {
    reqOptions.paymentStatus = queryOpts.paymentStatus;
  }

  if (sendLimit) {
    reqOptions.limit = queryOpts.limit;
  }
  return reqOptions;
}

export const clearOptions = () => dispatch => {
  dispatch({ type: CLEAR_TRANSACTION_OPTIONS });
};

export const loadTransactions = options => async (dispatch, getState) => {
  try {
    dispatch({ type: LOAD_TRANSACTIONS.start });

    dispatch({
      type: SAVE_TRANSACTION_OPTIONS,
      options
    });
    const reqOptions = getOptions(getState);

    const transaction = await getAllTransactions(reqOptions);

    return dispatch({
      type: LOAD_TRANSACTIONS.success,
      ...transaction
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: LOAD_TRANSACTIONS.failure });
  }
};

export const loadMovementsByContract = (
  contractId,
  status = [TransactionsStatus.PENDING, TransactionsStatus.COMPLETED],
  setState = () => {}
) => async dispatch => {
  try {
    dispatch({ type: LOAD_MOVEMENTS_BY_CONTACT.start });

    const movements = await getMovements({ contractId, status });

    setState(movements);

    return dispatch({ type: LOAD_MOVEMENTS_BY_CONTACT.success });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: LOAD_MOVEMENTS_BY_CONTACT.failure });
  }
};

export const loadClientTransaction = (id, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: LOAD_TRANSACTIONS.start });

    dispatch({
      type: SAVE_TRANSACTION_OPTIONS,
      options
    });
    const reqOptions = getOptions(getState);

    const transaction = await getTransactionById({
      id,
      options: reqOptions
    });

    return dispatch({
      type: LOAD_TRANSACTIONS.success,
      ...transaction
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: LOAD_TRANSACTIONS.failure });
  }
};

export const loadContractTransaction = (id, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: LOAD_CONTRACT_TRANSACTIONS.start });

    dispatch({
      type: SAVE_CONTRACT_TRANSACTION_OPTIONS,
      options
    });
    const reqOptions = getOptions(getState);

    const transaction = await getTransactionByContractId({
      id,
      options: reqOptions
    });

    return dispatch({
      type: LOAD_CONTRACT_TRANSACTIONS.success,
      ...transaction
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: LOAD_CONTRACT_TRANSACTIONS.failure });
  }
};

export const createManualTransaction = (
  values,
  handleClose = () => {}
) => async dispatch => {
  try {
    dispatch({ type: CREATE_MANUAL_TRANSACTION.start });

    if (values.type === TransactionsType.PAYMENT) {
      const data = {
        clientId: values.clientId,
        type: values.type,
        date: values.date,
        paymentMethod: values.paymentMethod,
        paymentForm: values.paymentForm,
        series: values.series,
        relatedInvoices: values.relatedInvoices,
        comment: values.comment,
        doNotBillTransaction: values.doNotBillTransaction
      };

      if (values.category === TransactionsCategory.DISCOUNT) {
        data.category = values.category;
      }

      await createTransaction(data);
    } else if (values.category === TransactionsCategory.UPFRONT_RENT) {
      // This is temporary until separate upfront rents to another window
      await createUpfrontRent(values);
    } else {
      await createTransaction(values);
    }

    dispatch(
      showMessage({
        message: 'CREATE_MANUAL_TRANSACTION',
        variant: 'success'
      })
    );
    dispatch(loadClientTransaction(values.clientId));
    handleClose();
    return dispatch({
      type: CREATE_MANUAL_TRANSACTION.success
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: CREATE_MANUAL_TRANSACTION.failure });
  }
};

export const downloadInvoice = id => async dispatch => {
  try {
    dispatch({ type: DOWNLOAD_INVOICE.start });

    const invoice = await downloadInvoiceById(id);

    return dispatch({
      type: DOWNLOAD_INVOICE.success,
      ...invoice
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: DOWNLOAD_INVOICE.failure });
  }
};

export const downloadTransactionsList = options => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: DOWNLOAD_TRANSACTIONS_LIST.start });

    dispatch({
      type: SAVE_TRANSACTION_OPTIONS,
      options
    });

    const reqOptions = getOptions(getState, false);

    await downloadExportedDistributors({
      ...reqOptions,
      customErrorMessage: 'TRANSACTIONS_NOT_FOUND'
    });

    dispatch(
      showMessage({
        message: 'DOWNLOAD_TRANSACTIONS_LIST',
        variant: SnackBarVariants.SUCCESS
      })
    );

    return dispatch({
      type: DOWNLOAD_TRANSACTIONS_LIST.success
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: DOWNLOAD_TRANSACTIONS_LIST.failure });
  }
};

export const assignTransactionToContract = (
  id,
  values,
  handleClose
) => async dispatch => {
  try {
    dispatch({ type: ASSIGN_TRANSACTION_TO_CONTRACT.start });

    await assignTransctionToEquipment({
      contractId: values.contract.id,
      transactionId: id
    });

    dispatch(
      showMessage({
        message: 'ASSIGN_TRANSACTION_TO_CONTRACT',
        variant: SnackBarVariants.SUCCESS
      })
    );

    handleClose();

    return dispatch({
      type: ASSIGN_TRANSACTION_TO_CONTRACT.success
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: ASSIGN_TRANSACTION_TO_CONTRACT.failure });
  }
};

export const downloadAccountStatmentResume =(id, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({
      type: DOWNLOAD_ACCOUNT_STATEMENT_RESUME.start
    });

    dispatch({
      type: SAVE_ACCOUNT_STATEMENT_OPTIONS,
      options
    });

    const reqOptions = {
      ...getOptions(getState, false),
      id, 
    };
    await downloadStatmentResume(reqOptions);

    return dispatch({
      type: DOWNLOAD_ACCOUNT_STATEMENT_RESUME.success
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: DOWNLOAD_ACCOUNT_STATEMENT_RESUME.failure });
  }
};

export const downloadClientTransactionsList = (clientId, options) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({
      type: DOWNLOAD_CLIENT_TRANSACTIONS.start
    });

    dispatch({
      type: SAVE_TRANSACTION_OPTIONS,
      options
    });

    const reqOptions = {
      ...getOptions(getState, false),
      clientId,
    };
    await downloadClientTransactions(reqOptions);

    dispatch(
      showMessage({
        message: 'DOWNLOAD_TRANSACTIONS_LIST',
        variant: SnackBarVariants.SUCCESS
      })
    );

    return dispatch({
      type: DOWNLOAD_CLIENT_TRANSACTIONS.success
    });
  } catch (error) {
    dispatch(
      showMessage({
        message: error.message,
        variant: SnackBarVariants.ERROR
      })
    );
    return dispatch({ type: DOWNLOAD_CLIENT_TRANSACTIONS.failure });
  }
};

export const loadTransactionDetail = (id, beforeDate) => async dispatch => {
  try {
    dispatch({ type: LOAD_TRANSACTION_DETAIL.start });

    const { item, complements, relatedInvoices } = await getTransactionDetailById({
      id,
      options: beforeDate ? { beforeDate } : {}
    });

    return dispatch({
      type: LOAD_TRANSACTION_DETAIL.success,
      item,
      complements,
      relatedInvoices
    });
  } catch (error) {
    dispatch(showMessage({ message: error.message, variant: 'error' }));
    return dispatch({ type: LOAD_TRANSACTION_DETAIL.failure });
  }
};

export const clearTransactionDetail = () => dispatch => {
  dispatch({ type: CLEAR_TRANSACTION_DETAIL });
};
